import { useIntl } from 'react-intl';
import { useRef } from 'react';
import FroalaEditor from 'froala-editor';
import PropTypes from 'prop-types';

import { useXssFilter } from '../../hooks';
import { FIELD_REPLACEMENT_SETS } from '../../constants';

import { STORE_LOGO } from './constants';
import styles from './index.module.less';
import {
  groupHeadingMessages,
  messages as fieldReplacementMessages,
  supportedInEmail,
  supportedInPackingSlips,
  supportedInPackingSlipsOrderItems,
  mainMessages,
} from './messages';

import { checkAndSetXssError } from '../../helpers';

const AVERAGE_PX_WIDTH_PER_CHARACTER = 8;

export const getSupportedGroups = (replacementSet) => {
  if (replacementSet === FIELD_REPLACEMENT_SETS.email) {
    return supportedInEmail;
  }
  if (replacementSet === FIELD_REPLACEMENT_SETS.packingSlip) {
    return supportedInPackingSlips;
  }
  return supportedInPackingSlipsOrderItems;
};

const RegisterFieldReplacementsButton = ({ onXssError, replacementSet }) => {
  const intl = useIntl();
  const codeMirror = useRef();
  const fieldReplacementOption = intl.formatMessage(mainMessages.buttonText);
  const xssFilter = useXssFilter();

  function froalaFieldReplacement(instance) {
    return {
      _init() {
        instance.opts.codeMirror.defineInitHook((cm) => {
          codeMirror.current = cm;

          codeMirror.current.on('blur', () => {
            if (instance.codeView.isActive()) {
              const cursor = codeMirror.current.getCursor();
              const content = codeMirror.current.getValue() || '';
              instance.html.set(content);
              // Setting the instance's html wipes out the previous cursor, reset it
              codeMirror.current.setCursor(cursor);
            }
          });

          codeMirror.current.on('change', () => {
            if (instance.codeView.isActive() && onXssError) {
              const content = codeMirror.current.getValue() || '';
              checkAndSetXssError(content, xssFilter, onXssError);
            }
          });
        });
      },

      _insertString(str) {
        if (instance.codeView.isActive()) {
          const cursor = codeMirror.current.getCursor();
          const htmlArray = codeMirror.current.getValue().split('\n');
          const line = htmlArray[cursor.line];
          const newLine = `${line.substring(0, cursor.ch)}${str}${line.substring(cursor.ch)}`;
          htmlArray[cursor.line] = newLine;
          instance.html.set(htmlArray.join('\n'));
          codeMirror.current.focus();
          // Setting the instance's html wipes out the previous cursor, reset it
          codeMirror.current.setCursor(cursor.line, cursor.ch + str.length);
        } else {
          instance.html.insert(str, false);
          instance.events.focus();
        }
      },

      insertReplacement(token) {
        this._insertString(`[${intl.formatMessage(fieldReplacementMessages[token])}]`);
      },

      insertStoreLogo() {
        this._insertString('<img style="vertical-align: middle;" src="http://images.shipstation.com/logo/00000" />');
      },
    };
  }
  FroalaEditor.PLUGINS.fieldReplacement = froalaFieldReplacement;
  FroalaEditor.RegisterCommand('fieldReplacement', {
    callback(_, val) {
      if (val === STORE_LOGO) {
        this.fieldReplacement.insertStoreLogo();
      } else {
        this.fieldReplacement.insertReplacement(val);
      }

      this.undo.saveStep();
    },
    defaultSelection() {
      return fieldReplacementOption;
    },
    displaySelection() {
      return true;
    },
    get displaySelectionWidth() {
      return fieldReplacementOption.length * AVERAGE_PX_WIDTH_PER_CHARACTER;
      // return this.fieldReplacement.displaySelectionWidth;
    },
    focus: true,
    html() {
      const supportedGroups = getSupportedGroups(replacementSet);
      return `<ul class="fr-dropdown-list" role="presentation">${Object.keys(supportedGroups)
        .map((groupKey) => {
          const optgroupHeading = `<li class="${styles.optgroup}" role="presentation">${intl.formatMessage(groupHeadingMessages[groupKey])}</li>`;
          const options = supportedGroups[groupKey].map((optionKey) => {
            const option = intl.formatMessage(fieldReplacementMessages[optionKey]);

            return `<li role="presentation">
              <a class="fr-command" tabindex="-1" role="option" data-cmd="fieldReplacement" data-param1="${optionKey}" title="${option}" aria-selected="false">${option}</a>
            </li>`;
          });

          return `${optgroupHeading}${options.join('')}`;
        })
        .join('')}</ul>`;
    },
    refreshAfterCallback: false,
    showOnMobile: true,
    title: fieldReplacementOption,
    type: 'dropdown',
    undo: true,
  });

  return null;
};

RegisterFieldReplacementsButton.propTypes = {
  onXssError: PropTypes.func,
  replacementSet: PropTypes.oneOf(Object.values(FIELD_REPLACEMENT_SETS)).isRequired,
};

export default RegisterFieldReplacementsButton;
